import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button } from '@mui/material';

import { Header, Popup, Table } from '../CommonComponents';
import { Container, Paper } from '../StyledComponents';
import { useDispatch, useSelector } from '../../redux/hooks';
import { selectTableOptions, setTableOptions } from '../../redux/actions/tables';
import {
    applyScrapedCompanies,
    enqueueTenantScrape,
    getScrapedCompanies
} from '../../api-admin/scraped-companies';
import toast from '../../utils/toast';
import { formatDateTime } from '../../utils/parsing';
import { jobLabel, ScrapedCompanyItem } from './format';
import ScrapedChanges from './ScrapedChanges';

const TABLE_NAME = 'scraped-companies';

type ConfirmState = {
    scope: 'selected' | 'all',
    mode: 'overwrite' | 'unedited',
    tenantIds: number[],
    count: number
};

function flagFromFilter(value: unknown): boolean | null {
    if (value === true || value === 'true') {
        return true;
    }
    if (value === false || value === 'false') {
        return false;
    }
    return null;
}

function listFilters(filterValues: Record<string, unknown> | undefined) {
    return {
        acceptedOnly: flagFromFilter(filterValues?.['isAccepted-equals']),
        failedOnly: flagFromFilter(filterValues?.['failed-equals']),
        hasChanges: flagFromFilter(filterValues?.['hasChanges-equals'])
    };
}

function isFailed(item: ScrapedCompanyItem) {
    return item.status === 'failed' || (!item.detailScrapedAt && !!item.lastError);
}

function differenceText(item: ScrapedCompanyItem) {
    const job = jobLabel(item);
    if (!item.detailScrapedAt) {
        const text = job ? `Inte skrapad. ${job}` : 'Inte skrapad';
        return (
            <Box
                component="span"
                title="Bolaget finns i skrapkön, men Allabolags bolagssida har inte hämtats ännu."
            >
                {text}
            </Box>
        );
    }
    return (
        <>
            <ScrapedChanges changes={item.changes} />
            {job && <Box component="span" sx={{ display: 'block' }}>{job}</Box>}
        </>
    );
}

export default function ScrapedCompanies() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const tableOptions = useSelector(selectTableOptions(TABLE_NAME));
    const filters = listFilters(tableOptions?.filterValues);
    const [items, setItems] = React.useState<ScrapedCompanyItem[]>([]);
    const [total, setTotal] = React.useState(0);
    const [page, setPage] = React.useState(1);
    const [isFetching, setIsFetching] = React.useState(false);
    const [confirm, setConfirm] = React.useState<ConfirmState | null>(null);
    const didInitAcceptedFilter = React.useRef(false);

    const load = React.useCallback(async (
        nextPage: number,
        acceptedOnly: boolean | null,
        failedOnly: boolean | null,
        hasChanges: boolean | null,
        orderBy?: string,
        orderDirection?: 'asc' | 'desc'
    ) => {
        setIsFetching(true);
        try {
            const data = await getScrapedCompanies({
                acceptedOnly,
                failedOnly,
                hasChanges,
                page: nextPage,
                orderBy,
                orderDirection
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setPage(data.page || nextPage);
        } catch (error) {
            console.error(error);
            toast(error, 'Kunde inte hämta skrapad data');
            setItems([]);
            setTotal(0);
        } finally {
            setIsFetching(false);
        }
    }, []);

    React.useEffect(() => {
        if (didInitAcceptedFilter.current) {
            return;
        }
        didInitAcceptedFilter.current = true;
        const filterValues = tableOptions?.filterValues || {};
        if (Object.keys(filterValues).length === 0) {
            dispatch(setTableOptions({
                name: TABLE_NAME,
                filterValues: { 'isAccepted-equals': true }
            }));
        }
    }, [dispatch, tableOptions?.filterValues]);

    const onServerChange = React.useCallback((options: {
        page: number,
        filterValues?: Record<string, unknown>,
        orderBy?: string,
        orderDirection?: 'asc' | 'desc'
    }) => {
        const next = listFilters(options.filterValues);
        load(
            options.page || 1,
            next.acceptedOnly,
            next.failedOnly,
            next.hasChanges,
            options.orderBy,
            options.orderDirection
        );
    }, [load]);

    const scrape = React.useCallback(async (tenantId: number) => {
        try {
            const result = await enqueueTenantScrape(tenantId);
            toast(result.status === 'alreadyPending' ? 'Redan köad' : 'Köad');
            load(
                page,
                filters.acceptedOnly,
                filters.failedOnly,
                filters.hasChanges,
                tableOptions?.orderBy,
                tableOptions?.orderDirection
            );
        } catch (error) {
            toast(error, 'Kunde inte köa skrapning');
        }
    }, [
        filters.acceptedOnly,
        filters.failedOnly,
        filters.hasChanges,
        load,
        page,
        tableOptions?.orderBy,
        tableOptions?.orderDirection
    ]);

    const columns = [{
        name: 'Företag',
        key: 'name'
    }, {
        name: 'Org. nummer',
        key: 'orgNumber'
    }, {
        name: 'Accepterat',
        key: 'isAccepted',
        type: 'bool' as const,
        canFilter: true
    }, {
        name: 'Misslyckad',
        key: 'failed',
        type: 'bool' as const,
        canFilter: true,
        isHidden: true,
        getValue: isFailed
    }, {
        name: 'Har skillnader',
        key: 'hasChanges',
        type: 'bool' as const,
        canFilter: true,
        isHidden: true,
        getValue: (item: ScrapedCompanyItem) => (
            !!item.detailScrapedAt && item.changes.length > 0
        )
    }, {
        name: 'Skrapad',
        key: 'detailScrapedAt',
        getValue: (item: ScrapedCompanyItem) => (
            item.detailScrapedAt ? formatDateTime(item.detailScrapedAt) : ''
        )
    }, {
        name: 'Skillnader',
        key: 'changes',
        whiteSpace: 'pre-line' as const,
        getValue: (item: ScrapedCompanyItem) => differenceText(item)
    }, {
        name: '',
        key: 'scrape',
        getValue: (item: ScrapedCompanyItem) => (
            <Button
                size="small"
                variant="outlined"
                onClick={(event) => {
                    event.stopPropagation();
                    scrape(item.tenantId);
                }}
            >
                Skrapa
            </Button>
        )
    }];

    const ask = (scope: ConfirmState['scope'], mode: ConfirmState['mode'], tenantIds: number[], count: number) => {
        setConfirm({
            scope, mode, tenantIds, count
        });
    };

    return (
        <Container>
            <Header
                title="Skrapad Data"
                hideBackButton
                CustomComponent={(
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            onClick={() => ask('all', 'overwrite', [], total)}
                        >
                            Uppdatera alla
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => ask('all', 'unedited', [], total)}
                        >
                            Uppdatera orörda fält för alla
                        </Button>
                    </Box>
                )}
            />
            <Paper padding={0}>
                <Table
                    name={TABLE_NAME}
                    columns={columns}
                    data={items}
                    keyField="tenantId"
                    defaultOrderBy="name"
                    defaultOrderDirection="asc"
                    showPagination
                    serverTotal={total}
                    onServerChange={onServerChange}
                    onRowClick={(item: ScrapedCompanyItem) => navigate(`/tenants/${item.tenantId}`)}
                    emptyText={isFetching ? 'Hämtar skrapad data...' : 'Ingen skrapad data.'}
                    batchActions={[{
                        name: 'Uppdatera valda',
                        action: async (selected: ScrapedCompanyItem[]) => {
                            ask('selected', 'overwrite', selected.map((item) => item.tenantId), selected.length);
                        }
                    }, {
                        name: 'Uppdatera orörda fält för valda',
                        action: async (selected: ScrapedCompanyItem[]) => {
                            ask('selected', 'unedited', selected.map((item) => item.tenantId), selected.length);
                        }
                    }]}
                />
            </Paper>
            <Popup
                open={!!confirm}
                title="Uppdatera från skrapad data"
                body={confirm
                    ? `Vill du uppdatera ${confirm.count} företag?`
                    : ''}
                okLabel="Uppdatera"
                handleClose={() => setConfirm(null)}
                handleOk={async () => {
                    if (!confirm) {
                        return;
                    }
                    try {
                        const result = await applyScrapedCompanies({
                            tenantIds: confirm.tenantIds,
                            scope: confirm.scope,
                            mode: confirm.mode,
                            acceptedOnly: filters.acceptedOnly,
                            failedOnly: filters.failedOnly,
                            hasChanges: filters.hasChanges
                        });
                        toast(`Uppdaterade ${result.updated}, överhoppade ${result.skipped}`);
                        setConfirm(null);
                        load(
                            page,
                            filters.acceptedOnly,
                            filters.failedOnly,
                            filters.hasChanges,
                            tableOptions?.orderBy,
                            tableOptions?.orderDirection
                        );
                    } catch (error) {
                        toast(error, 'Kunde inte uppdatera');
                    }
                }}
            />
        </Container>
    );
}
